/**
 * LogoutConfirmModal — the standard sign-out confirmation shared by every
 * entry point that ends the session (sidebar, mobile menu, account settings).
 *
 * It also owns the navigate-before-logout order: leaving the member page after
 * the session drops lets ProtectedRoute bounce the visitor to /login instead of
 * the public landing page.
 */
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from '../feed/ConfirmModal';

interface LogoutConfirmModalProps {
  /** Dismiss the dialog; also called once the member confirms. */
  onClose: () => void;
  /** Runs on confirm before the session drops, e.g. to close the host menu. */
  onConfirmed?: () => void;
}

export function LogoutConfirmModal({ onClose, onConfirmed }: LogoutConfirmModalProps) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleConfirm = async (): Promise<void> => {
    onClose();
    onConfirmed?.();
    navigate('/', { replace: true });
    await logout();
  };

  return (
    <ConfirmModal
      title={t('accountSettings.logoutConfirm.title')}
      message={t('accountSettings.logoutConfirm.message')}
      confirmLabel={t('accountSettings.logoutConfirm.yes')}
      cancelLabel={t('accountSettings.logoutConfirm.no')}
      showIcon
      onClose={onClose}
      onConfirm={() => { handleConfirm().catch(() => undefined); }}
    />
  );
}
